import { useContext, useEffect, useRef } from "react"
import type { MeetingPanelEventPayload } from "services/api/meetingsClient"
import { ToolOwnershipContext, ToolRenewalContext } from "./call/shared/toolOwnership"

export const REALTIME_RECONNECT_BASE_MS = 2000
export const REALTIME_RECONNECT_MAX_MS = 30000
export const BOUND_RENEW_MIN_MS = 30000

type UseMeetingPanelEventsOptions = {
  realtimeUrl?: string | null
  enabled?: boolean
  onEvent: (event: MeetingPanelEventPayload) => void
  onReconnect?: () => void
  /** Bound native capability only; ordinary sockets retain their existing lifecycle. */
  renewalKey?: string
  panelToken?: string
}

export function useMeetingPanelEvents({ realtimeUrl, enabled = true, onEvent, onReconnect, renewalKey, panelToken }: UseMeetingPanelEventsOptions) {
  const inherited = useContext(ToolOwnershipContext)
  const renewable = useContext(ToolRenewalContext)
  const owner = renewalKey || (renewable ? inherited : null)
  let url = realtimeUrl || ''
  let stream = url
  let token = ''
  try {
    const parsed = new URL(url)
    if (panelToken) parsed.searchParams.set('panel_token', panelToken)
    token = parsed.searchParams.get('panel_token') || ''
    url = parsed.toString()
    parsed.searchParams.delete('panel_token')
    if (owner) stream = `${owner}:${parsed.toString()}`
  } catch { /* Existing empty/malformed URL handling remains with the socket. */ }
  const current = useRef({ stream, url, token }); current.current = { stream, url, token }
  const onEventRef = useRef(onEvent); onEventRef.current = onEvent
  const onReconnectRef = useRef(onReconnect); onReconnectRef.current = onReconnect
  const socketRef = useRef<WebSocket | null>(null)
  const sentToken = useRef('')
  const lastRenewAt = useRef<number | null>(null)
  const lastSignature = useRef('')

  useEffect(() => {
    if (!enabled || !url || typeof window === 'undefined' || !window.WebSocket) return
    let disposed = false
    let socket: WebSocket | null = null
    let attempt = 0
    let opened = false
    let retryTimer: number | undefined
    const owns = () => !disposed && current.current.stream === stream
    const connect = () => {
      if (!owns()) return
      socket = new window.WebSocket(current.current.url)
      socketRef.current = socket
      lastRenewAt.current = null
      sentToken.current = current.current.token
      socket.onopen = () => {
        if (!owns()) return
        attempt = 0
        if (opened) onReconnectRef.current?.()
        opened = true
        if (owner && current.current.token !== sentToken.current) {
          socket?.send(JSON.stringify({ type: 'meeting_panel.renew', panel_token: current.current.token }))
          sentToken.current = current.current.token
          lastRenewAt.current = Date.now()
        }
      }
      socket.onmessage = event => {
        if (!owns()) return
        try {
          const payload = JSON.parse(String(event.data || '{}')) as MeetingPanelEventPayload
          const signature = String(event.data)
          if (signature === lastSignature.current) return
          lastSignature.current = signature
          if (payload?.type) onEventRef.current(payload)
        } catch { /* Malformed events are not authority. */ }
      }
      socket.onerror = () => undefined
      socket.onclose = () => {
        if (!owns()) return
        retryTimer = window.setTimeout(connect, Math.min(REALTIME_RECONNECT_BASE_MS * 2 ** attempt++, REALTIME_RECONNECT_MAX_MS))
      }
    }
    connect()
    return () => {
      disposed = true; window.clearTimeout(retryTimer)
      if (socket) { socket.onopen = null; socket.onmessage = null; socket.onerror = null; socket.onclose = null; socket.close() }
      socketRef.current = null
    }
  // A bound capability changes on the existing socket; logical ownership/path changes reconnect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, stream])
  useEffect(() => {
    const socket = socketRef.current
    if (!owner || socket?.readyState !== 1 || !token || sentToken.current === token) return
    // Coalesce rapid same-owner movement/refresh renewals. The existing proof
    // remains short-lived; this never extends its authority or the grant.
    const delay = lastRenewAt.current === null ? 0 : Math.max(0, BOUND_RENEW_MIN_MS - (Date.now() - lastRenewAt.current))
    const renew = () => {
      if (socketRef.current !== socket || current.current.token !== token || socket.readyState !== 1) return
      socket.send(JSON.stringify({ type: 'meeting_panel.renew', panel_token: token }))
      sentToken.current = token
      lastRenewAt.current = Date.now()
    }
    if (!delay) { renew(); return }
    const timer = window.setTimeout(renew, delay)
    return () => window.clearTimeout(timer)
  }, [owner, token])
}
